import type { Reference } from "src/db";
import type { FeedbackSchema } from "../MetadataFeedback/types";

const safeDecodeUriComponent = (value: unknown): string => {
    if (typeof value !== "string") {
        return "";
    }

    try {
        return decodeURIComponent(value);
    } catch {
        return value;
    }
};

const _toNumber = (value: unknown): number | undefined => {
    if (typeof value === "number" && Number.isFinite(value)) {
        return value;
    }

    if (typeof value === "string" && value.trim() !== "") {
        const parsed = Number(value);
        if (Number.isFinite(parsed)) {
            return parsed;
        }
    }

    return undefined;
};

const mapToReference = (item: unknown, fallbackIndex: number): Reference | undefined => {
    if (!item || typeof item !== "object") {
        return undefined;
    }

    const candidate = item as Record<string, unknown>;
    const resolvedIndex = fallbackIndex + 1;
    const rawReferenceId = candidate.reference_id ?? candidate.referenceId;
    const title = typeof candidate.title === "string" ? candidate.title : "";
    const source = safeDecodeUriComponent(candidate.source);
    const dataSourceType =
        candidate.dataSourceType ?? candidate.data_source_type ?? candidate.datasource_type;

    const normalizedReferenceId =
        typeof rawReferenceId === "string"
            ? rawReferenceId
            : typeof rawReferenceId === "number"
              ? String(rawReferenceId)
              : String(resolvedIndex);

    if (!normalizedReferenceId) {
        return undefined;
    }

    return {
        reference_id: normalizedReferenceId,
        title,
        source,
        dataSourceType: typeof dataSourceType === "string" ? dataSourceType : "",
    } satisfies Reference;
};

export const normalizeReferences = (documents: unknown): Reference[] | undefined => {
    if (!Array.isArray(documents) || documents.length === 0) {
        return undefined;
    }

    const normalized = documents
        .map((item, index) => mapToReference(item, index))
        .filter((item): item is Reference => Boolean(item));

    return normalized.length > 0 ? normalized : undefined;
};

const extractFallbackDocuments = (rowData: FeedbackSchema | null) => {
    const source = rowData?._source as Record<string, unknown> | undefined;
    if (!source) {
        return undefined;
    }

    return source["cts_answer.documents"];
};

export const extractReferencesFromFeedback = (
    rowData: FeedbackSchema | null,
): Reference[] | undefined => {
    const primaryDocuments = rowData?._source?.cts_answer?.documents;
    if (primaryDocuments && primaryDocuments.length > 0) {
        return normalizeReferences(primaryDocuments);
    }

    const fallbackDocuments = extractFallbackDocuments(rowData);
    return normalizeReferences(fallbackDocuments);
};
